import { useEffect, useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { logError } from '@/utils/errorUtils'
import { useOrpc } from './useClient'

const PAGE_SIZE = 50

export type PractitionerRow = Awaited<ReturnType<OrpcClient['listPractitioners']>>['data'][number]

export type PractitionerRoleFilter = NonNullable<
  Parameters<OrpcClient['listPractitioners']>[0]['role']
>

type UsePractitionersOptions = {
  search?: string
  role?: PractitionerRoleFilter
  nameSort?: 'asc' | 'desc'
}

/**
 * The Org Members of one Organization (`listPractitioners`), in pages of fifty for infinite
 * scroll. Shared by the Provider Operations list and Super → Organizations → Open Members;
 * the middleware refuses an Organization outside the caller's scope.
 */
export function usePractitioners(
  organizationId: string,
  { search = '', role, nameSort }: UsePractitionersOptions = {},
) {
  const orpc = useOrpc()

  const query = useInfiniteQuery(
    orpc.listPractitioners.infiniteOptions({
      input: (offset: number) => ({
        organizationId,
        search: search.trim() || undefined,
        role,
        order: nameSort ? [{ name: nameSort }] : undefined,
        page: { offset, count: PAGE_SIZE, limit: Number.MAX_SAFE_INTEGER },
      }),
      initialPageParam: 0,
      // Walk by the requested page, not by rows received: `total` counts every matched
      // member, including any the middleware skipped, so a short page must still advance.
      getNextPageParam: (lastPage) => {
        const next = lastPage.page.offset + lastPage.page.count
        return next < lastPage.page.total ? next : undefined
      },
      enabled: !!organizationId,
      // Keep the current results on screen while a new search loads so the
      // table doesn't collapse to a loader and back (no layout jump).
      placeholderData: keepPreviousData,
    }),
  )

  useEffect(() => {
    if (query.error) {
      logError('practitioner', 'Failed to load members', query.error)
      showNotification({
        color: 'red',
        title: 'Failed to load members',
        message: query.error instanceof Error ? query.error.message : 'Unknown error',
      })
    }
  }, [query.error])

  const practitioners = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.data),
    [query.data],
  )

  const total = query.data?.pages.at(-1)?.page.total ?? 0

  return { ...query, practitioners, total }
}
